import { useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, TextField } from "@mui/material";

/**
 * Compact from/to date-range filter that syncs to the same JSON `filter` URL
 * param used by MultiSelectFilter. Writes scalar `date_from` / `date_to`
 * (YYYY-MM-DD) keys, which the dashboard forwards to the analytics endpoint.
 * An empty field clears that bound (unbounded on that side).
 */
export const DateRangeFilter = ({
  fromSource = "date_from",
  toSource = "date_to",
}: {
  fromSource?: string;
  toSource?: string;
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  let currentFilters: Record<string, string | string[]> = {};
  try {
    const filterParam = searchParams.get("filter");
    currentFilters = filterParam ? JSON.parse(filterParam) : {};
  } catch {
    currentFilters = {};
  }

  const fromValue = typeof currentFilters[fromSource] === "string" ? (currentFilters[fromSource] as string) : "";
  const toValue = typeof currentFilters[toSource] === "string" ? (currentFilters[toSource] as string) : "";

  const setBound = useCallback(
    (source: string, value: string) => {
      const params = new URLSearchParams(searchParams);
      const newFilters = { ...currentFilters };

      if (value) {
        newFilters[source] = value;
      } else {
        delete newFilters[source];
      }

      if (Object.keys(newFilters).length === 0) {
        params.delete("filter");
      } else {
        params.set("filter", JSON.stringify(newFilters));
      }

      void navigate({ search: params.toString() }, { replace: true });
    },
    [searchParams, currentFilters, navigate]
  );

  // Match the Carrier/Client dropdowns exactly: those Autocomplete inputs measure
  // 35.4px tall, so pin the date boxes to the same height (border-box) with the
  // native control filling it, transparent so it inherits the grey filter-bar band.
  const fieldSx = {
    width: 180,
    "& .MuiInputBase-root": { height: 35.4, boxSizing: "border-box", fontSize: "0.8rem", py: 0 },
    "& .MuiInputBase-input": { height: "100%", boxSizing: "border-box", py: 0 },
    "& .MuiInputLabel-root": { fontSize: "0.8rem" },
  };

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <TextField
        type="date"
        size="small"
        variant="outlined"
        label="From"
        value={fromValue}
        onChange={(e) => setBound(fromSource, e.target.value)}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: toValue || undefined } }}
        sx={fieldSx}
      />
      <TextField
        type="date"
        size="small"
        variant="outlined"
        label="To"
        value={toValue}
        onChange={(e) => setBound(toSource, e.target.value)}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: fromValue || undefined } }}
        sx={fieldSx}
      />
    </Box>
  );
};
